

import { ArrowBackIcon, CloseIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  IconButton,
  SimpleGrid,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Alert,
  AlertIcon,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Center,
  Badge,
} from "@chakra-ui/react";
import { Tooltip } from "antd";
import {
  Asset,
  Calibration,
  getAssetById,
  getCalibrationsByAssetId,
  getCalibrationHistoryByAssetId,
  getHeaderContext,
  HeaderContext,
  CalibrationHistory,
} from "api/assetDetailRepository";
import { useRef } from "react";
import { useAuth } from 'contexts/authContext';
import React, { useEffect, useState } from "react";
import { MdVisibility } from "react-icons/md";
import { useLocation, useNavigate } from "react-router-dom";
import { assetRepository } from "api/assetRepository";


const InfoTile = ({
  title,
  details,
}: {
  title: string;
  details: { label: string; value: string | undefined | null }[];
}) => {
  return (
    <Box
      borderWidth="1px"
      borderRadius="lg"
      p={4}
      boxShadow="sm"
      bgGradient="linear(to-b, #246bf8ff, #2868e8ff)"

    >
      <Text fontWeight="semibold" fontSize="md" mb={2} color="white">
        {title}
      </Text>
      {details.map((d, i) => (
        <Text key={i} fontSize="sm" color="white">
          {d.label}: <strong>{d.value ?? "—"}</strong>
        </Text>
      ))}
    </Box>
  );
};

const AssetDetail: React.FC = () => {
  const location = useLocation();
  const assetId = (location.state as { assetId: string })?.assetId;
  const navigate = useNavigate();
  const initialAsset = location.state as Asset | undefined;
  type AssetWithLease = Asset & {
    leased?: {
      contractorName?: string;
      leaseStartDate?: string;
      leaseEndDate?: string;
    } | null;
  };

  const [asset, setAsset] = useState<AssetWithLease | null>(initialAsset ?? null);
  // const [asset, setAsset] = useState<Asset | null>(initialAsset ?? null);
  const [calibrations, setCalibrations] = useState<Calibration[]>([]);
  const [calibrationHistory, setCalibrationHistory] = useState<CalibrationHistory[]>([]);
  const [headerContext, setHeaderContext] = useState<HeaderContext | null>(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [loading, setLoading] = useState(!initialAsset);
  const [error, setError] = useState<string | null>(null);

  const bgContentBox = useColorModeValue("gray.50", "navy.700");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.200");
  const textColor = useColorModeValue("gray.700", "white");
  const boxBg = useColorModeValue("white", "gray.800");

  const [unleaseCandidate, setUnleaseCandidate] = useState<string | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const [centerAlertVisible, setCenterAlertVisible] = useState(false);
  const [centerAlertMessage, setCenterAlertMessage] = useState("");
  const [centerAlertStatus, setCenterAlertStatus] = useState<
    "success" | "error" | "warning"
  >("success");

  const { user, role } = useAuth();
  const {
    orgId,
    locationId,
    vendorId,
    departmentId,
  } = location.state || {};
  const handleAddCalibration = (asset: Asset) => {
    navigate("/admin/Calibration/NewRecord", { state: { asset } });
  };
  const [isUnleasing, setIsUnleasing] = useState(false);

  useEffect(() => {
    const loadHeaderContext = async () => {
      try {
        const header = await getHeaderContext(
          orgId,
          locationId,
          assetId,
          vendorId,
          departmentId
        );

        setHeaderContext(header);
      } catch (error) {
        console.error(error);
      }
    };

    if (
      orgId &&
      locationId &&
      assetId &&
      vendorId &&
      departmentId
    ) {
      loadHeaderContext();
    }
  }, [orgId, locationId, assetId, vendorId, departmentId]);

  useEffect(() => {
    if (!assetId) return;

    getCalibrationsByAssetId(assetId)
      .then((data) => setCalibrations(data || []))
      .catch((err) => console.error(err));

    getCalibrationHistoryByAssetId(assetId)
      .then((data) => setCalibrationHistory(data || []))
      .catch((err) => console.error(err));
  }, [assetId]);

  if (loading)
    return (
      <Flex justify="center" align="center" minH="100vh">
        <Spinner size="xl" />
      </Flex>
    );

  if (error) return <Text color="red.500">{error}</Text>;
  if (!asset) return <Text>No asset found</Text>;

  const showCenteredAlert = (
    message: string,
    status: "success" | "error" | "warning" = "success"
  ) => {
    setCenterAlertMessage(message);
    setCenterAlertStatus(status);
    setCenterAlertVisible(true);

    setTimeout(() => {
      setCenterAlertVisible(false);
    }, 2000);
  };
  const confirmUnlease = async () => {
    if (!unleaseCandidate) return;

    try {
      setIsUnleasing(true);

      await assetRepository.unleaseAssetFromContractor(unleaseCandidate);

      showCenteredAlert("Asset unleased successfully", "success");

      setUnleaseCandidate(null);

      setAsset((prev) =>
        prev ? { ...prev, leased: null } : prev
      );

    } catch (err: any) {
      showCenteredAlert(
        err?.response?.data || "Failed to unlease asset",
        "error"
      );
    } finally {
      setIsUnleasing(false);
    }
  };
  const pendingCalibrations = calibrations.filter(
    (cal) =>
      cal.status?.toLowerCase() === "pending" ||
      cal.status?.toLowerCase() === "active"
  );

  const completedCalibrations = calibrations.filter(
    (cal) => cal.status?.toLowerCase() === "completed"
  );
  return (
    <Box minH="100vh" px={{ base: 4, md: 6 }} pt="100px">
      <Box
        borderWidth="1px"
        borderRadius="lg"
        borderColor={borderColor}
        p={{ base: 4, md: 6 }}
        boxShadow="lg"
        bg={boxBg}
      >
        <Flex justify="space-between" align="start" mb={6}>
          <Text fontSize="sm" color="gray.500">
            Dashboard / Assets / <strong>{asset.name}</strong>
          </Text>

          <Flex gap={2} align="center">
            {role !== 'Vendor' && (
              <Button
                colorScheme="blue"
                variant="solid"
                borderRadius="16px"
                size="sm"
                onClick={() =>
                  navigate(`/admin/asset-management/EditAsset`, { state: asset })
                }
              >
                Edit
              </Button>
            )}

            <IconButton
              icon={<CloseIcon />}
              aria-label="Close"
              variant="ghost"
              size="sm"
              onClick={() => navigate("/admin/asset-management/assetList", {
                state:
                  location.state,

              })}
            />
          </Flex>
        </Flex>

        {/* Header Tiles Section */}
        {headerContext && (
          <SimpleGrid columns={{ base: 1, md: 2, lg: 5 }} spacing={4} mb={6}>
            <InfoTile
              title="Organization"
              details={[
                { label: "Name", value: headerContext.orgInfo?.name },
                { label: "GSTIN", value: headerContext.orgInfo?.gsTin },
                { label: "Contact", value: headerContext.orgInfo?.contact },
              ]}
            />
            <InfoTile
              title="Plant"
              details={[
                { label: "Name", value: headerContext.locationInfo?.name },
                { label: "City", value: headerContext.locationInfo?.city },
                { label: "State", value: headerContext.locationInfo?.state },
                { label: "Zip", value: headerContext.locationInfo?.zipcode },
              ]}
            />
            <InfoTile
              title="Asset"
              details={[
                { label: "Name", value: headerContext.assetInfo?.name },
                { label: "Type", value: headerContext.assetInfo?.type },
                { label: "Status", value: headerContext.assetInfo?.status },
                { label: "Eqp Id", value: headerContext.assetInfo?.assetEquipmentId },
              ]}
            />
            <InfoTile
              title="Vendor"
              details={[
                { label: "Name", value: headerContext.vendorInfo?.name },
                { label: "GSTIN", value: headerContext.vendorInfo?.gsTin },
                { label: "Contact", value: headerContext.vendorInfo?.contactNumber },
              ]}
            />
            <InfoTile
              title="Department"
              details={[
                { label: "Name", value: headerContext.departmentInfo?.name },
              ]}
            />
          </SimpleGrid>
        )}
        {/* Current Calibrations */}
        <Box
          borderWidth="1px"
          borderRadius="lg"
          borderColor={borderColor}
          bg={bgContentBox}
          p={6}
          mb={6}
          boxShadow="sm"
        >
          <Flex justify="space-between" align="center" mb={4}>
            <Text fontWeight="semibold" fontSize="lg" color={textColor}>
              Current Calibrations
            </Text>
            {/* Show Add Calibration only if no pending or history */}
            {role !== 'Vendor' && pendingCalibrations.length === 0 && calibrationHistory.length === 0 && (
              <Button
                variant="solid"
                colorScheme="blue"
                onClick={() => asset && handleAddCalibration(asset)}
              >
                Add Calibration
              </Button>
            )}
          </Flex>

          <Table variant="simple" size="md">
            <Thead>
              <Tr>
                <Th w="25%">New Calibration Date</Th>
                <Th w="25%">Calibration Due Date</Th>
                <Th w="15%">Status</Th>
                <Th w="20%">Performed By</Th>
                <Th w="15%">Notes</Th>
              </Tr>
            </Thead>
            <Tbody>
              {pendingCalibrations.length > 0 ? (
                pendingCalibrations.map((cal) => (
                  <Tr key={cal.calibrationId}>
                    <Td>
                      {cal.calibrationDate
                        ? new Date(cal.calibrationDate).toLocaleDateString()
                        : "—"}
                    </Td>
                    <Td>
                      {cal.calibrationDueDate
                        ? new Date(cal.calibrationDueDate).toLocaleDateString()
                        : "—"}
                    </Td>
                    <Td>{cal.status ?? "—"}</Td>
                    <Td>{cal.contactName || "N/A"}</Td>
                    <Td>{cal.notes || "N/A"}</Td>
                  </Tr>
                ))
              ) : (
                <Tr>
                  <Td colSpan={4}>
                    <Text>No current calibrations available for this asset</Text>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </Box>
        {/* Calibration History Section */}
        <Box
          borderWidth="1px"
          borderRadius="lg"
          borderColor={borderColor}
          bg={bgContentBox}
          p={6}
          mb={6}
          boxShadow="sm"
        >
          <Flex justify="space-between" align="center" mb={4}>
            <Text fontWeight="semibold" fontSize="lg" color={textColor}>
              Calibration History
            </Text>
          </Flex>

          <Table variant="simple" size="md">
            <Thead>
              <Tr>
                <Th w="25%">Old Calibration Date</Th>
                <Th w="25%">Calibrated Date</Th>
                <Th w="15%">Status</Th>
                <Th w="20%">Performed By</Th>
                <Th w="15%">Notes</Th>
                <Th w="15%">Certificates</Th>
              </Tr>
            </Thead>
            <Tbody>
              {calibrationHistory.length > 0 ? (
                calibrationHistory.map((cal) => (
                  <Tr key={cal.id}>
                    {/* {completedCalibrations.length > 0 ? (
            completedCalibrations.map((cal) => (
              <Tr key={cal.calibrationId}> */}
                    <Td>
                      {cal.calibrationDate
                        ? new Date(cal.calibrationDueDate).toLocaleDateString()
                        : "—"}
                    </Td>
                    <Td>
                      {cal.calibratedDate
                        ? new Date(cal.calibratedDate).toLocaleDateString()
                        : "—"}
                    </Td>
                    <Td>{cal.status ?? "—"}</Td>
                    <Td>{cal.contactName || "N/A"}</Td>
                    <Td>{cal.notes || "N/A"}</Td>
                    <Td>
                      <Tooltip title="View Certificate">
                        <IconButton
                          icon={<MdVisibility size={20} />}
                          aria-label="View Certificate"
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            navigate("/admin/CertificateDetails", {
                              state: { id: cal.certificateId },
                            })
                          }
                        />
                      </Tooltip>
                    </Td>

                  </Tr>
                ))
              ) : (
                <Tr>
                  <Td colSpan={4}>
                    <Text>No calibration history available for this asset</Text>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </Box>
        {asset.leased && (
          <Box
            borderWidth="1px"
            borderRadius="lg"
            borderColor={borderColor}
            bg={bgContentBox}
            p={6}
            mb={6}
            boxShadow="sm"
          >
            <Flex justify="space-between" align="center" mb={4}>
              <Text fontWeight="semibold" fontSize="lg" color={textColor}>
                Lease Details
              </Text>

              {role !== "Vendor" && (
                <Flex gap={2}>
                  {/* Renew */}
                  <Button
                    size="sm"
                    colorScheme="blue"
                    onClick={() =>
                      navigate("/admin/asset-management/AddAsset", {
                        state: {
                          mode: "renew",
                          asset: asset,
                          isRenew: true,
                        },
                      })
                    }
                  >
                    Renew
                  </Button>

                  {/* Unlease */}
                  <Button
                    size="sm"
                    colorScheme="red"
                    onClick={() => setUnleaseCandidate(asset.assetId)}
                  >
                    Unlease
                  </Button>
                </Flex>
              )}
            </Flex>

            <Table variant="simple" size="md">
              <Thead>
                <Tr>
                  <Th w="25%">Contractor</Th>
                  <Th w="25%">Lease Start Date</Th>
                  <Th w="25%">Lease End Date</Th>
                  <Th w="25%">Status</Th>
                </Tr>
              </Thead>

              <Tbody>
                <Tr>
                  <Td>{asset.leased?.contractorName || "—"}</Td>

                  <Td>
                    {asset.leased?.leaseStartDate
                      ? new Date(asset.leased.leaseStartDate).toLocaleDateString()
                      : "—"}
                  </Td>

                  <Td>
                    {asset.leased?.leaseEndDate
                      ? new Date(asset.leased.leaseEndDate).toLocaleDateString()
                      : "—"}
                  </Td>

                  <Td>
                    <Badge colorScheme="purple">
                      Active Lease
                    </Badge>
                  </Td>
                </Tr>
              </Tbody>
            </Table>
          </Box>
        )}

        <AlertDialog
          isOpen={!!unleaseCandidate}
          leastDestructiveRef={cancelRef}
          onClose={() => setUnleaseCandidate(null)}
          isCentered
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Unlease Asset
              </AlertDialogHeader>

              <AlertDialogBody>
                Are you sure you want to unlease this asset from contractor?
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={() => setUnleaseCandidate(null)}>
                  Cancel
                </Button>

                <Button
                  colorScheme="red"
                  onClick={confirmUnlease}
                  ml={3}
                  isLoading={isUnleasing}
                >
                  Unlease
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>

        {centerAlertVisible && (
          <Center
            position="fixed"
            top="0"
            left="0"
            right="0"
            bottom="0"
            zIndex={60}
            pointerEvents="none"
          >
            <Alert
              status={centerAlertStatus}
              variant="solid"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              width="600px"
              borderRadius="14px"
              boxShadow="xl"
              py={10}
              px={16}
              pointerEvents="auto"
            >
              <AlertIcon boxSize="40px" mr={0} />
              <Text fontSize="lg" fontWeight="700" mt={1}>
                {centerAlertMessage}
              </Text>
            </Alert>
          </Center>
        )}
      </Box>
    </Box>
  );
};

export default AssetDetail;